import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useReviewCriteria } from "@/hooks/use-review-criteria";
import {
  type ReviewTemplateDetail,
  useDeleteReviewTemplate,
  useReviewRelationshipTypes,
  useReviewTemplate,
  useSetReviewTemplateCriteria,
  useSetReviewTemplateRelationships,
  useSetReviewTemplateScopes,
  useUpdateReviewTemplate,
} from "@/hooks/use-review-templates";
import { ReviewQuestionDialog } from "./ReviewQuestionDialog";
import { ReviewQuestionPicker } from "./ReviewQuestionPicker";

/**
 * The review form builder — EPIC-047, WI-160/161/163/164.
 *
 * Three sections, each saved whole: what is rated, who it applies to, and who
 * is asked. That mirrors the server, which replaces each collection outright —
 * there is deliberately no way to leave a form half-edited.
 *
 * **Once a cycle using a form opens, the form locks.** Everything below goes
 * read-only at that point, because the answers already recorded point at these
 * exact criteria; a late edit would make two people's reviews incomparable.
 */

/** Ticked on a form nobody has saved yet — the two a round actually generates. */
const DEFAULT_ASKED = new Set(["self", "manager"]);

export function ReviewFormBuilder({ templateId }: { templateId: string }) {
  const { data, isLoading, isError, error, refetch } = useReviewTemplate(templateId);
  /**
   * Fetched HERE, not inside the editor, so the editor is handed a complete
   * starting state exactly once.
   *
   * The reviewer-type catalogue used to load inside the editor and arrive a
   * moment after it first rendered, which made the draft change under itself.
   * Once the editor started refusing to overwrite unsaved work — which it
   * must — that left the section empty for the rest of the session and SAVED
   * it empty, wiping the form's reviewer configuration. Waiting for both
   * queries costs a few milliseconds of skeleton and removes the race instead
   * of handling it.
   */
  const { data: relationshipTypes } = useReviewRelationshipTypes();

  if (isError) {
    return (
      <ErrorState
        message={
          error instanceof Error
            ? `Couldn't load this form: ${error.message}`
            : "Couldn't load this form"
        }
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data || !relationshipTypes) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const locked = Boolean(data.lockedAt);

  return (
    <div className="space-y-6">
      {locked && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
          <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">This form is locked</p>
            <p className="text-muted-foreground">
              A review cycle using it has opened, so its questions can no longer change — the
              answers already given were made against these exact criteria. Make a copy if you need
              a different version.
            </p>
          </div>
        </div>
      )}

      <ReadinessBanner template={data} />

      <FormEditor template={data} locked={locked} relationshipTypes={relationshipTypes} />
    </div>
  );
}

/**
 * Whether to replace the on-screen draft with what the server just sent.
 *
 * Exported and pure so the rule can be tested: it is three booleans, and
 * getting the third one wrong silently destroys work. Which it did — the
 * reviewer-type catalogue is a second query that resolves AFTER this page
 * first renders, so the server fingerprint changes a moment later, and taking
 * that as "the server changed" reset every draft on the page. Five questions
 * added in that window went without a word.
 */
export function shouldReseed(
  /** What we last seeded from, or null on first render. */
  seeded: string | null,
  /** What the server holds now. */
  fromServer: string,
  /** Whether the person has unsaved changes on screen. */
  dirty: boolean,
): boolean {
  if (seeded === null) return true;
  if (seeded === fromServer) return false;
  // The server moved AND there is unsaved work. Leave the work alone: losing
  // it silently is worse than showing a slightly stale form, and saving is
  // what reconciles the two.
  return !dirty;
}

/**
 * The form's content, with client-only bookkeeping stripped — what "has this
 * changed?" actually means.
 *
 * A scope row carries `key`, the server row's id, so React has something
 * stable to render by. Saving DELETES and re-INSERTS those rows, so the ids
 * come back different every time even when nothing about the form changed —
 * and comparing them left "You have unsaved changes" on screen permanently,
 * however many times you pressed Save.
 */
export function contentOf(
  details: DetailsDraft,
  criteria: CriterionDraft[],
  scopes: ScopeDraft[],
  relationships: RelationshipDraft[],
): string {
  return fingerprint([
    details,
    criteria.map((c) => [c.criterionId, c.weight, c.isRequired, c.relationshipKey, c.visibility]),
    // Half-filled rows are dropped on save, so they must not read as content
    // here either, or adding an empty row would look like unsaved work.
    scopes.filter((sc) => sc.roleId !== null).map((sc) => [sc.roleId, sc.roleLevelId]),
    relationships.map((r) => [r.relationshipKey, r.isEnabled, r.instructions]),
  ]);
}

interface DetailsDraft {
  name: string;
  description: string;
  status: ReviewTemplateDetail["status"];
}

function detailsFrom(t: ReviewTemplateDetail): DetailsDraft {
  return { name: t.name, description: t.description ?? "", status: t.status };
}

function criteriaFrom(t: ReviewTemplateDetail): CriterionDraft[] {
  return t.criteria.map((c) => ({
    criterionId: c.criterionId,
    title: c.title,
    weight: c.weight,
    isRequired: c.isRequired,
    relationshipKey: c.relationshipKey ?? null,
    visibility: c.visibility,
  }));
}

function scopesFrom(t: ReviewTemplateDetail): ScopeDraft[] {
  return t.scopes.map((sc) => ({
    key: sc.id,
    roleId: sc.roleId,
    roleLevelId: sc.roleLevelId,
  }));
}

/**
 * Every relationship gets a row whether or not the form has saved one yet —
 * otherwise a new form shows nothing to switch on. Which means this needs the
 * catalogue, not just the form, and so it is seeded once the types arrive.
 *
 * A form that has NEVER been saved starts with self and manager ticked: they
 * are the two every round asks, the only two generation can fill today, and
 * an all-unticked default meant a fully built form quietly asked nobody. A
 * form saved with them deliberately off stays off — `relationships` being
 * empty is what tells "new" from "emptied on purpose".
 */
function relationshipsFrom(t: ReviewTemplateDetail, types: { key: string }[]): RelationshipDraft[] {
  const neverSaved = t.relationships.length === 0;
  return types.map((type) => {
    const existing = t.relationships.find((r) => r.relationshipKey === type.key);
    return {
      relationshipKey: type.key,
      isEnabled: existing?.isEnabled ?? (neverSaved && DEFAULT_ASKED.has(type.key)),
      instructions: existing?.instructions ?? "",
    };
  });
}

/**
 * One form, one Save.
 *
 * The four parts of a form used to save themselves, which meant four buttons on
 * one page and no answer to "have I saved this yet". The draft for all four
 * lives here now and one button commits it — Matt's call on 2026-09-25, over
 * autosaving, because finishing a form is a deliberate act in a way that
 * answering one is not.
 *
 * Each part is still its own component; they are controlled rather than
 * self-saving.
 */
function FormEditor({
  template,
  locked,
  relationshipTypes,
}: {
  template: ReviewTemplateDetail;
  locked: boolean;
  /** Already loaded — the page waits for it, so the draft is seeded once. */
  relationshipTypes: { key: string; name: string }[];
}) {
  const saveCriteria = useSetReviewTemplateCriteria(template.id);
  const saveScopes = useSetReviewTemplateScopes(template.id);
  const saveRelationships = useSetReviewTemplateRelationships(template.id);
  const saveDetails = useUpdateReviewTemplate(template.id);

  const [details, setDetails] = useState<DetailsDraft>(() => detailsFrom(template));
  const [criteria, setCriteria] = useState<CriterionDraft[]>(() => criteriaFrom(template));
  const [scopes, setScopes] = useState<ScopeDraft[]>(() => scopesFrom(template));
  const [relationships, setRelationships] = useState<RelationshipDraft[]>(() =>
    relationshipsFrom(template, relationshipTypes),
  );

  /**
   * Re-seed from the server only when the SERVER's version actually changed.
   *
   * Refetching on window focus used to discard whatever was half-typed; the
   * fingerprint is what tells a real change apart from the same data arriving
   * again.
   */
  /**
   * What the server last gave us, and what we have now.
   *
   * `dirty` drives both the save bar and the guard below — a form page with
   * unsaved work has to say so, because the first person to use this added
   * five questions, never saw the Save button at the top of a page he had
   * scrolled down, and lost all of it.
   */
  const fromServer = contentOf(
    detailsFrom(template),
    criteriaFrom(template),
    scopesFrom(template),
    relationshipsFrom(template, relationshipTypes),
  );
  const current = contentOf(details, criteria, scopes, relationships);
  const dirty = !locked && current !== fromServer;

  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!shouldReseed(seeded.current, fromServer, dirty)) return;
    seeded.current = fromServer;
    setDetails(detailsFrom(template));
    setCriteria(criteriaFrom(template));
    setScopes(scopesFrom(template));
    setRelationships(relationshipsFrom(template, relationshipTypes));
  }, [fromServer, dirty, template, relationshipTypes]);

  /** The browser's own "are you sure" — the last line against closing the tab. */
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function discard() {
    setDetails(detailsFrom(template));
    setCriteria(criteriaFrom(template));
    setScopes(scopesFrom(template));
    setRelationships(relationshipsFrom(template, relationshipTypes));
    seeded.current = fromServer;
  }

  const pending =
    saveCriteria.isPending ||
    saveScopes.isPending ||
    saveRelationships.isPending ||
    saveDetails.isPending;

  async function saveAll() {
    // Sequential, not parallel: four writes against one form, and a failure
    // part-way through should stop rather than race the rest.
    await saveDetails.mutateAsync(details);
    await saveCriteria.mutateAsync({
      criteria: criteria.map((c) => ({
        criterionId: c.criterionId,
        weight: c.weight,
        isRequired: c.isRequired,
        relationshipKey: c.relationshipKey,
        visibility: c.visibility,
      })),
    });
    await saveScopes.mutateAsync({
      // A half-filled row — a level chosen before its role — is not a scope
      // yet, and dropping it here is kinder than refusing the whole save.
      scopes: scopes
        .filter((sc): sc is ScopeDraft & { roleId: string } => typeof sc.roleId === "string")
        .map((sc) => ({ roleId: sc.roleId, roleLevelId: sc.roleLevelId })),
    });
    /**
     * EVERY relationship, including the ones left unticked.
     *
     * Saving only the ticked ones left the table empty for a form that asks
     * nobody — and an empty table is exactly how this page decides a form is
     * brand new and helpfully pre-ticks self and manager. So the form came
     * back with two ticks it had just been saved without, the draft and the
     * server never matched, and "You have unsaved changes" would not go away
     * however many times you pressed Save.
     *
     * Storing the unticked rows is safe: generation filters on `is_enabled`
     * in its own query, and the audit diff lists only the enabled ones.
     */
    await saveRelationships.mutateAsync({
      relationships: relationships.map((r) => ({
        relationshipKey: r.relationshipKey,
        isEnabled: r.isEnabled,
        instructions: r.instructions,
      })),
    });
  }

  return (
    <div className="space-y-6">
      <FormDetailsSection
        template={template}
        locked={locked}
        details={details}
        onChange={setDetails}
      />
      <CriteriaSection
        locked={locked}
        rows={criteria}
        onChange={setCriteria}
        relationshipTypes={relationshipTypes}
      />
      <ScopeSection locked={locked} rows={scopes} onChange={setScopes} />
      <ReviewersSection locked={locked} rows={relationships} onChange={setRelationships} />
      {/*
        Pinned to the bottom of the scroll area rather than sitting at the top
        of the page. The extra right padding keeps Save clear of the Nymbl
        feedback widget, which is `position: fixed` in the bottom-right corner
        at z-index 2147480000 — it cannot be stacked above, so the button has
        to step aside. Without it the click lands on the widget and nothing
        saves, which is indistinguishable from the page being broken. Questions are added a long way down a form, and a Save
        button you have to scroll back up to find is one you do not press —
        which is exactly how the first person to use this lost five of them.

        The negative margin and the `after:` filler are the pattern
        `PersonProfile` and `PeopleTargetsPage` already use: `main` is the
        scroll container and carries its own padding, so a sticky child has to
        reach past it or the content shows through underneath.
      */}
      {dirty && (
        <div className="-mx-4 md:-mx-6 sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 border-border border-t bg-background px-4 pt-3 pr-24 pb-1 after:absolute after:inset-x-0 after:top-full after:h-4 after:bg-background md:px-6 md:pr-28 md:after:h-6">
          <span className="font-medium text-sm">You have unsaved changes.</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" disabled={pending} onClick={discard}>
              Discard
            </Button>
            <Button disabled={pending} onClick={() => void saveAll()}>
              {pending ? "Saving…" : "Save form"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Why this form is not yet reaching anybody, said on the form itself.
 *
 * A new form is inert in two independent ways and neither was visible here: it
 * starts as a draft (drafts are never generated from) and every relationship
 * starts unticked, so "Save reviewers" on a fresh form stores nothing and the
 * form asks nobody. Both are correct defaults. Finding out about them from a
 * round that covered nobody, with the reason "No form covers this role", was
 * not.
 */
function ReadinessBanner({ template }: { template: ReviewTemplateDetail }) {
  const blockers: string[] = [];
  if (template.status === "draft") blockers.push("it is still a draft");
  if (template.status === "archived") blockers.push("it is archived");
  if (template.scopes.length === 0) blockers.push("no roles are named on it");
  if (!template.relationships.some((r) => r.isEnabled)) blockers.push("nobody is asked");
  // No "it has no questions" blocker: since WI-309 the questions are the
  // subject's role competencies, resolved at generation. The form's own
  // questions are optional extras, so an empty list is the normal case and
  // generation never skips a form for it.

  const clashes = template.clashes;
  if (blockers.length === 0 && clashes.length === 0) return null;

  return (
    <div className="space-y-3">
      {blockers.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">This form won't be used yet</p>
            <p className="text-muted-foreground">
              A round will pass it by because {listWords(blockers)}.
            </p>
          </div>
        </div>
      )}

      {clashes.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 p-4">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">Another form claims the same people</p>
            <p className="text-muted-foreground">
              Only one form can apply to a person, and the newer one wins. Overlapping with{" "}
              {listWords([...new Set(clashes.flatMap((c) => c.otherTemplateNames))])}. Archive the
              one you have finished with, or narrow the roles on this one.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** "a, b and c" — read out loud in a sentence, so Oxford commas are wrong here. */
function listWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function FormDetailsSection({
  template,
  locked,
  details,
  onChange,
}: {
  template: ReviewTemplateDetail;
  locked: boolean;
  details: DetailsDraft;
  onChange: (next: DetailsDraft) => void;
}) {
  const navigate = useNavigate();
  const nameId = useId();
  const descriptionId = useId();
  const remove = useDeleteReviewTemplate();
  const { name, description, status } = details;

  return (
    <DetailSection
      title="Form details"
      headerActions={
        <div className="flex items-center gap-2">
          {/*
            Deleting is offered even though the server may refuse: it refuses
            only for a form a cycle or review already points at, and says so.
            Hiding the button would need the client to duplicate that rule.
          */}
          <ConfirmDialog
            trigger={
              <Button variant="outline" size="sm" disabled={locked || remove.isPending}>
                <Trash2 className="size-4" aria-hidden />
                Delete
              </Button>
            }
            title="Delete this review form?"
            description={`"${template.name}" and everything it asks will be removed. A form any review cycle has already used can't be deleted — archive it instead.`}
            confirmLabel="Delete form"
            onConfirm={async () => {
              await remove.mutateAsync(template.id);
              navigate({ to: "/dashboard/reviews/forms" });
            }}
          />
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={nameId}>Name</Label>
          <Input
            id={nameId}
            value={name}
            disabled={locked}
            onChange={(e) => onChange({ ...details, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={status}
            disabled={locked}
            onValueChange={(next) =>
              onChange({ ...details, status: next as ReviewTemplateDetail["status"] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft — not used by any cycle</SelectItem>
              <SelectItem value="active">Active — cycles can generate from it</SelectItem>
              <SelectItem value="archived">Archived — kept for history</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor={descriptionId}>Description</Label>
          <Textarea
            id={descriptionId}
            value={description}
            disabled={locked}
            rows={2}
            onChange={(e) => onChange({ ...details, description: e.target.value })}
          />
        </div>
      </div>
    </DetailSection>
  );
}

interface CriterionDraft {
  criterionId: string;
  title: string;
  weight: number;
  isRequired: boolean;
  /** Null = asked of everyone; a key = asked only of that relationship. */
  relationshipKey: string | null;
  visibility: "all_readers" | "people_ops";
}

/**
 * A stable fingerprint of what the server sent.
 *
 * Each section below seeds a local draft from the template and re-seeds it when
 * the server's copy changes. The dependency used to be the parsed array itself,
 * which is a NEW object on every fetch — so any refetch re-ran the effect and
 * discarded whatever was being typed. React Query refetches on window focus
 * with a 30s stale time, so alt-tabbing lost the draft; saving one section
 * invalidates the whole template, so it lost the other sections' drafts too.
 *
 * Comparing content rather than identity means a refetch that changed nothing
 * leaves the draft alone. A refetch that DID change something still wins — that
 * is a real edit from elsewhere, and silently keeping a stale draft over it
 * would be worse.
 */
function fingerprint(value: unknown): string {
  return JSON.stringify(value);
}

/** The sentinel for "asked of everyone" — a Select cannot hold an empty value. */
const ALL_REVIEWERS = "__all__";

function CriteriaSection({
  locked,
  rows,
  onChange,
  relationshipTypes,
}: {
  locked: boolean;
  rows: CriterionDraft[];
  onChange: (next: CriterionDraft[]) => void;
  relationshipTypes: { key: string; name: string }[];
}) {
  // One base id per section; each row derives its own from the index. A `useId`
  // per row would be a hook inside a loop.
  const requiredIdBase = useId();
  const setRows = onChange;
  const [picking, setPicking] = useState(false);
  const [writing, setWriting] = useState(false);
  const catalogue = useReviewCriteria(true);

  const move = (index: number, delta: number) => {
    const next = [...rows];
    const target = index + delta;
    const row = next[index];
    const swap = next[target];
    if (!row || !swap) return;
    next[index] = swap;
    next[target] = row;
    setRows(next);
  };

  return (
    <DetailSection
      title="Questions"
      headerActions={
        !locked && (
          <div className="flex items-center gap-2">
            {/*
              A real button, not a dropdown that looks like a search box. The
              combobox WAS the add control and it read as a filter — the first
              person to build a form here could not find how to add anything.
            */}
            <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
              <Plus className="size-4" aria-hidden />
              Add a question
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWriting(true)}>
              Write a new one
            </Button>
          </div>
        )
      }
    >
      <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
        Extra questions, asked UNDER the competencies the person's role brings in — those are pulled
        in automatically and are not listed here. Questions are shared across forms, so wording one
        here words it everywhere.
      </p>

      {rows.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">Nothing added yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-border rounded-lg border border-border">
          {rows.map((row, index) => (
            <li key={row.criterionId} className="flex flex-wrap items-center gap-3 p-3">
              <span className="w-6 shrink-0 text-sm tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 font-medium">{row.title}</span>

              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${requiredIdBase}-${index}`}
                  checked={row.isRequired}
                  disabled={locked}
                  onCheckedChange={(checked) =>
                    setRows(
                      rows.map((r, i) =>
                        i === index ? { ...r, isRequired: checked === true } : r,
                      ),
                    )
                  }
                />
                <Label
                  htmlFor={`${requiredIdBase}-${index}`}
                  className="cursor-pointer font-normal text-muted-foreground"
                >
                  Answer required
                </Label>
              </div>

              {/*
                Who is asked, and who may read it — WI-310.

                Both sit on the ROW rather than behind an "advanced" toggle:
                the question this exists for (promotion and salary readiness,
                asked only of a manager and read only by People Ops) needs
                both, and a confidential question that silently defaults to
                visible is the failure worth designing against.
              */}
              <Select
                value={row.relationshipKey ?? ALL_REVIEWERS}
                disabled={locked}
                onValueChange={(value) =>
                  setRows(
                    rows.map((r, i) =>
                      i === index
                        ? { ...r, relationshipKey: value === ALL_REVIEWERS ? null : value }
                        : r,
                    ),
                  )
                }
              >
                <SelectTrigger className="w-44" aria-label={`Who answers "${row.title}"`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_REVIEWERS}>Everyone</SelectItem>
                  {relationshipTypes.map((type) => (
                    <SelectItem key={type.key} value={type.key}>
                      {type.name} only
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={row.visibility}
                disabled={locked}
                onValueChange={(value) =>
                  setRows(
                    rows.map((r, i) =>
                      i === index
                        ? {
                            ...r,
                            visibility: value === "people_ops" ? "people_ops" : "all_readers",
                          }
                        : r,
                    ),
                  )
                }
              >
                <SelectTrigger className="w-48" aria-label={`Who can read "${row.title}"`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_readers">Shown on the report</SelectItem>
                  <SelectItem value="people_ops">People Ops only</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={locked || index === 0}
                  aria-label={`Move ${row.title} up`}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp className="size-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={locked || index === rows.length - 1}
                  aria-label={`Move ${row.title} down`}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="size-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={locked}
                  aria-label={`Remove ${row.title}`}
                  onClick={() => setRows(rows.filter((_, i) => i !== index))}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!locked && (
        <ReviewQuestionPicker
          open={picking}
          onOpenChange={setPicking}
          options={(catalogue.data ?? []).filter((q) => !rows.some((r) => r.criterionId === q.id))}
          onAdd={(questions) =>
            setRows([
              ...rows,
              ...questions.map((question) => ({
                criterionId: question.id,
                title: question.title,
                weight: question.defaultWeight,
                isRequired: true,
                relationshipKey: null,
                visibility: "all_readers" as const,
              })),
            ])
          }
          onWriteNew={() => {
            setPicking(false);
            setWriting(true);
          }}
        />
      )}
      {/*
        Writing a question from inside the form is the whole point of the
        second button: the reason the empty form felt like a dead end was that
        the question you wanted had to exist already, somewhere else.
      */}
      <ReviewQuestionDialog
        open={writing}
        question={null}
        onOpenChange={setWriting}
        onCreated={(id, title) => {
          // Appended from the CURRENT list, not the one captured when this
          // dialog rendered — the catalogue refetches as the question is
          // created, and the stale copy is how the picker lost four of five.
          setRows([
            ...rows,
            {
              criterionId: id,
              title,
              weight: 1,
              isRequired: true,
              relationshipKey: null,
              visibility: "all_readers",
            },
          ]);
        }}
      />
    </DetailSection>
  );
}

interface ScopeDraft {
  /** Client-only row identity, so an unsaved blank row has a stable React key. */
  key: string;
  roleId: string | null;
  roleLevelId: string | null;
}

function ScopeSection({
  locked,
  rows,
  onChange,
}: {
  locked: boolean;
  rows: ScopeDraft[];
  onChange: (next: ScopeDraft[]) => void;
}) {
  const setRows = onChange;

  return (
    <DetailSection title="Who this form is for">
      <p className="mb-4 text-sm text-muted-foreground">
        A role with no level set covers everyone in that role. If another form names the same role
        at a specific level, that more specific one wins for those people.
      </p>

      <ul className="mb-4 space-y-2">
        {rows.map((row, index) => (
          <li key={row.key} className="flex flex-wrap items-center gap-2">
            <EntityCombobox
              entity="roles"
              value={row.roleId}
              disabled={locked}
              onChange={(roleId) =>
                setRows(rows.map((r, i) => (i === index ? { ...r, roleId } : r)))
              }
              filters={{ status: "active" }}
              placeholder="Choose a role"
              className="w-64"
            />
            <EntityCombobox
              entity="role-levels"
              value={row.roleLevelId}
              disabled={locked}
              includeNone
              noneLabel="Any level"
              onChange={(roleLevelId) =>
                setRows(rows.map((r, i) => (i === index ? { ...r, roleLevelId } : r)))
              }
              placeholder="Any level"
              className="w-56"
            />
            <Button
              variant="ghost"
              size="icon"
              disabled={locked}
              aria-label="Remove this role"
              onClick={() => setRows(rows.filter((_, i) => i !== index))}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>

      {!locked && (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setRows([...rows, { key: crypto.randomUUID(), roleId: null, roleLevelId: null }])
          }
        >
          <Plus className="size-4" aria-hidden />
          Add a role
        </Button>
      )}
    </DetailSection>
  );
}

interface RelationshipDraft {
  relationshipKey: string;
  isEnabled: boolean;
  instructions: string;
}

function ReviewersSection({
  locked,
  rows,
  onChange,
}: {
  locked: boolean;
  rows: RelationshipDraft[];
  onChange: (next: RelationshipDraft[]) => void;
}) {
  const { data: types } = useReviewRelationshipTypes();
  const setRows = onChange;

  // Seeded by the parent, which owns the draft for the whole form.

  return (
    <DetailSection title="Who is asked">
      <ul className="divide-y divide-border rounded-lg border border-border">
        {rows.map((row, index) => {
          const type = types?.find((t) => t.key === row.relationshipKey);
          return (
            <li key={row.relationshipKey} className="space-y-3 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Checkbox
                  checked={row.isEnabled}
                  disabled={locked}
                  aria-label={`Ask ${type?.name ?? row.relationshipKey}`}
                  onCheckedChange={(checked) =>
                    setRows(
                      rows.map((r, i) => (i === index ? { ...r, isEnabled: checked === true } : r)),
                    )
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{type?.name ?? row.relationshipKey}</p>
                  {type?.description && (
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  )}
                </div>
                {row.isEnabled && <StatusPill tone="success" label="Asked" />}
              </div>

              {row.isEnabled && (
                <div className="space-y-3 pl-8">
                  <Textarea
                    value={row.instructions}
                    disabled={locked}
                    rows={2}
                    placeholder="What this reviewer is told before they start (optional)"
                    onChange={(e) =>
                      setRows(
                        rows.map((r, i) =>
                          i === index ? { ...r, instructions: e.target.value } : r,
                        ),
                      )
                    }
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </DetailSection>
  );
}
